import * as React from 'react'
import Link from '@mui/material/Link'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import LanguageIcon from '@mui/icons-material/Language'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import HelpIcon from '@mui/icons-material/HelpOutlined'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooksOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import ScatterPlotOutlinedIcon from '@mui/icons-material/ScatterPlotOutlined'
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import Settings from '@mui/icons-material/Settings'
import Logout from '@mui/icons-material/Logout'
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined'
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined'
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'

import { UserContext } from '../../UserContext'
import { resetAnalyticsIdentity } from '../../Analytics'
import { toggleExperimentalFeatures } from '../../hooks/useApi'
import routes from '../../routes'
import { appMarketingSiteLabel, isKeywordGraphApp } from '../../appVariant'
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined'
import { planLabelFor, usesInfranodusLabels } from '../../planLabels'
import { usePaywallStore } from '../Paywall/usePaywallStore'

export default function MenuItemsAuthUser() {
    const {
        username = '',
        email = '',
        inlanguage: language = '',
        experimentalEnabled = false,
        flagsLoading: isLoadingFlags = false,
        isAdmin = false,
        tier = null,
        plan = '',
        infranodus_uid = null,
    } = React.useContext(UserContext)
    // The boot payload's tier goes stale the moment an embedded checkout
    // completes; the usage store is re-read right after, so it wins.
    const usageTier = usePaywallStore(state => state.usage?.tier || null)
    const effectiveTier = usageTier || tier

    const isKeywordGraph = isKeywordGraphApp()

    const handleExperimentalToggle = async event => {
        event.preventDefault()
        event.stopPropagation()

        try {
            // Toggle to the opposite state
            const data = await toggleExperimentalFeatures(!experimentalEnabled)
            if (data.success) {
                // Reload the page to activate/deactivate experimental features
                window.location.reload()
            }
        } catch (error) {
            console.error('Error toggling experimental features:', error)
        }
    }

    // Legacy (pre-3D-migration) InfraNodus runs on its own subdomain.
    // Mirror the current path so the user lands on the same graph
    const legacyHref =
        'https://legacy.infranodus.com' +
        (typeof window !== 'undefined'
            ? window.location.pathname + window.location.search
            : '')

    return [
        <MenuItem disabled key="username">
            <Typography variant="subtitle2" component="div">
                Username: {username}
            </Typography>
        </MenuItem>,
        <MenuItem disabled key="email">
            <Typography variant="subtitle2" component="div">
                Email: {email}
            </Typography>
        </MenuItem>,
        <Divider key="divider-language" />,
        <MenuItem key="language" component={Link} href={routes.settings}>
            <ListItemIcon>
                <LanguageIcon fontSize="small" />
            </ListItemIcon>
            Language: {language}
        </MenuItem>,
        <Divider key="divider-settings" />,
        <MenuItem key="settings" component={Link} href={routes.settings}>
            <ListItemIcon>
                <Settings fontSize="small" />
            </ListItemIcon>
            Settings
        </MenuItem>,
        // The account menu is the only proactive way into the
        // upgrade dialog. Free → "Upgrade"; paid → plan name + Manage; a
        // session without a tier (backend not sending it yet) keeps today's item.
        effectiveTier === 'FREE' ? (
            <MenuItem
                key="upgrade"
                component={Link}
                href={routes.upgrade}
                data-testid="menu-upgrade"
            >
                <ListItemIcon>
                    <WorkspacePremiumOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Upgrade
            </MenuItem>
        ) : (
            <MenuItem
                component={Link}
                href={routes.subscription}
                key="subscription"
                data-testid="menu-subscription"
            >
                <ListItemIcon>
                    <CardMembershipOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {effectiveTier
                    ? `${planLabelFor(
                          plan,
                          usesInfranodusLabels(infranodus_uid)
                      )} · Manage`
                    : 'Subscription'}
            </MenuItem>
        ),
        <MenuItem component={Link} href={routes.apiAccess} key="api-access">
            <ListItemIcon>
                <ApiOutlinedIcon fontSize="small" />
            </ListItemIcon>
            API & MCP
        </MenuItem>,
        isAdmin ? (
            <MenuItem
                component={Link}
                href={routes.adminPanel}
                key="admin-panel"
            >
                <ListItemIcon>
                    <AdminPanelSettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Admin Panel
            </MenuItem>
        ) : null,
        <Divider key="divider-guide" />,
        <MenuItem
            component={Link}
            href={isKeywordGraph ? '/learn' : routes.userGuide}
            key="user-guide"
        >
            <ListItemIcon>
                <LibraryBooksIcon fontSize="small" />
            </ListItemIcon>
            User Guide
        </MenuItem>,
        <MenuItem component={Link} href={routes.support} key="support">
            <ListItemIcon>
                <HelpIcon fontSize="small" />
            </ListItemIcon>
            Support
        </MenuItem>,
        <MenuItem component={Link} href={routes.methodology} key="methodology">
            <ListItemIcon>
                <InfoIcon fontSize="small" />
            </ListItemIcon>
            Methodology
        </MenuItem>,
        <MenuItem
            component={Link}
            href={routes.joinCommunity}
            key="joinCommunity"
        >
            <ListItemIcon>
                <PeopleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Join the Community
        </MenuItem>,
        <MenuItem
            key="experimental-features"
            onClick={handleExperimentalToggle}
            disabled={isLoadingFlags}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={experimentalEnabled}
                        size="small"
                        onClick={e => e.stopPropagation()}
                        sx={{ paddingLeft: 0 }}
                    />
                }
                label="Enable Experimental Features"
                sx={{ margin: 0, pointerEvents: 'none' }}
            />
        </MenuItem>,
        <Divider key="divider-info" />,
        isKeywordGraph ? null : (
            <MenuItem
                component={Link}
                href={routes.aboutInfranodus}
                key="aboutInfranodus"
            >
                <ListItemIcon>
                    <PsychologyAltOutlinedIcon fontSize="small" />
                </ListItemIcon>
                About InfraNodus
            </MenuItem>
        ),
        <MenuItem
            component={Link}
            href={routes.infranodus}
            key="marketing-site"
        >
            <ListItemIcon>
                <ScatterPlotOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {appMarketingSiteLabel()}
        </MenuItem>,
        isKeywordGraph ? null : <Divider key="divider-legacy" />,
        isKeywordGraph ? null : (
            <MenuItem component={Link} href={legacyHref} key="switch-legacy">
                <ListItemIcon>
                    <RestoreOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Switch to legacy InfraNodus
            </MenuItem>
        ),
        <Divider key="divider-login" />,
        <MenuItem
            component={Link}
            href={routes.switchAccount}
            onClick={resetAnalyticsIdentity}
            key="switchAccount"
        >
            <ListItemIcon>
                <LoopOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Switch Account
        </MenuItem>,
        <MenuItem
            component={Link}
            href={routes.logout}
            onClick={resetAnalyticsIdentity}
            key="logout"
        >
            <ListItemIcon>
                <Logout fontSize="small" />
            </ListItemIcon>
            Logout
        </MenuItem>,
    ]
}
